import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthData, Token } from 'commons';
import { DateTime } from 'luxon';
import { lastValueFrom, Observable } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';
import { CONFIG } from '../../config';
import { ILoginCredentials } from '../../shared/interfaces';
import { LocalStorageService } from '../services/local-storage.service';
import { ExceptionService } from '../services/exception.service';
import { EventsHubService } from '../services/events-hub.service';


@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {

  private readonly loginPath = `${CONFIG.apiPath}/oauth/token`;

  private readonly EXPIRATION_MARGIN_MINUTES = 5;

  constructor(
    private readonly httpClient: HttpClient,
    private readonly exceptionService: ExceptionService,
    private readonly localStorageService: LocalStorageService,
    private readonly eventsHubService: EventsHubService,
  ) {
    const authData = this.authData;
    if (authData) {
      this.eventsHubService.setLoggedIn(authData);
    } else {
      this.logout();
    }
  }

  public async login(formDataCredentials: ILoginCredentials): Promise<AuthData> {
    try {
      const response = await lastValueFrom(this.loginCall(formDataCredentials));
      const loginResponse: Token = response.body;

      if (!loginResponse) throw new Error("Empty login response");

      const expiresAt = DateTime.now().plus({ seconds: loginResponse.expires_in }).toISO();
      loginResponse.expires_at = expiresAt;
      const authData: AuthData = {
        tokenInfo: loginResponse,
        traceId: response.headers.get('X-Trace-Id'),
        user: null,
        selectedTrip: null,
      };
      return authData;

    } catch (error) {
      console.error("Login failed", error);
      throw error;
    }
  }

  private loginCall(formDataCredentials: ILoginCredentials): Observable<any> {
    let body = new URLSearchParams();
    body.set('grant_type', formDataCredentials.grant_type);
    body.set('client_id', formDataCredentials.client_id);
    body.set('client_secret', formDataCredentials.client_secret);
    body.set('username', formDataCredentials.username);
    body.set('password', formDataCredentials.password);
    return this.httpClient.post(this.loginPath, body, { observe: 'response' }).pipe(timeout(CONFIG.httpTimeout),
      catchError(ex => this.exceptionService.catchBadResponse(ex)));
  }

  public setAuthData(authData: AuthData): void {
    this.localStorageService.set('auth-data', authData);
    this.eventsHubService.setLoggedIn(authData);
  }

  public get authData(): AuthData {
    return this.localStorageService.get('auth-data', null);
  }

  public logout(): void {
    this.localStorageService.delete('auth-data');
    this.eventsHubService.setLoggedIn(null);
  }

  public isTokenValid(): boolean {
    return this.authData?.tokenInfo?.access_token !== undefined && !this.isTokenExpired(this.authData.tokenInfo.expires_at);
  }

  public isTokenExpired(expiresAtISO: string | null | undefined): boolean {
    if (!expiresAtISO) return true;
    const tokenExpiresAt = DateTime.fromISO(expiresAtISO).minus({ minutes: this.EXPIRATION_MARGIN_MINUTES });
    return DateTime.now() > tokenExpiresAt;
  }
}
